<template>
    <div class="create-release-form p-fluid pt-2">
        <div class="p-field">
            <label for="modal-release-name" class="form-label"
                >Release Name</label
            >
            <InputText
                id="modal-release-name"
                v-model="releaseName"
                placeholder="e.g., v1.0.0, release-2024-01"
                class="w-full"
                aria-required="true"
                autofocus
            />
            <small class="form-help-text">
                Choose a unique name for this release. Avoid spaces and special
                characters.
            </small>
            <small v-if="validationError" class="form-error-text">
                {{ validationError }}
            </small>
        </div>
        <div class="p-field">
            <label for="modal-release-description" class="form-label">
                Description (Optional)
            </label>
            <Textarea
                id="modal-release-description"
                v-model="description"
                placeholder="Release notes or description of changes..."
                class="w-full"
                rows="4"
                autoResize
            />
            <small class="form-help-text">
                Add release notes or describe what's included in this release.
            </small>
        </div>
    </div>
    <div class="buttons">
        <Button
            label="Cancel"
            icon="pi pi-times"
            class="p-button-text"
            @click="dialogRef?.close"
        />
        <Button
            label="Create Release"
            icon="pi pi-tag"
            @click="createRelease"
            :disabled="!isFormValid || isLoading"
            :loading="isLoading"
        />
    </div>
</template>

<script setup lang="ts">
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import Textarea from "primevue/textarea";
import { Ref, computed, inject, ref } from "vue";

import { useReleaseManager, useReleaseStore } from "@/stores/release-store";

const dialogRef = inject<Ref<DynamicDialogInstance> | undefined>("dialogRef");
const releaseStore = useReleaseStore();
const { isLoading } = useReleaseManager();

// Form fields
const releaseName = ref("");
const description = ref("");

// Invalid characters for git refs
const invalidChars = [" ", "~", "^", ":", "?", "*", "[", "\\", "..", "@{"];

// Form validation
const validationError = computed(() => {
    if (!releaseName.value) return "";

    for (const char of invalidChars) {
        if (releaseName.value.includes(char)) {
            return `Release name cannot contain '${char}'`;
        }
    }

    return "";
});

const isFormValid = computed(() => {
    if (!releaseName.value) return false;
    if (validationError.value) return false;

    return true;
});

const createRelease = async () => {
    if (!isFormValid.value) return;

    try {
        const result = await releaseStore.createRelease(
            releaseName.value,
            description.value || undefined
        );

        if (result) {
            dialogRef?.value.close({
                data: {
                    success: true,
                    release: result,
                },
            });
        }
    } catch (error) {
        console.error("Error creating release:", error);
        dialogRef?.value.close({ data: { success: false }, error });
    }
};
</script>

<style scoped lang="scss">
.create-release-form {
    margin: 0;
    padding: 0.5rem 0 1rem;
}

.p-field {
    display: flex;
    flex-direction: column;
    margin-bottom: 1.5rem;
}

.form-label {
    margin-bottom: 0.75rem;
    font-weight: 600;
    display: block;
}

.form-help-text {
    display: block;
    color: #6c757d;
    margin-top: 0.75rem;
    font-size: 0.875rem;
}

.form-error-text {
    display: block;
    color: #dc3545;
    margin-top: 0.5rem;
    font-size: 0.875rem;
}

.buttons {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
}

.w-full {
    width: 100%;
}
</style>
